// API service for communicating with the backend server
import { GPT, User, Prompt, GPTRegistration, SuggestedPrompt, Category } from '../types';

const API_BASE_URL = '/api';

// Helper function to convert database fields to frontend format
const mapGPTFromDB = (dbGPT: any): GPT => ({
  id: dbGPT.id,
  name: dbGPT.name,
  description: dbGPT.description,
  useCases: dbGPT.use_cases || [],
  tags: dbGPT.tags || [],
  category: dbGPT.category,
  chatGptUrl: dbGPT.chat_gpt_url,
  isPinned: dbGPT.is_pinned || false,
  isActive: dbGPT.is_active !== false, // Default to true if not set
  gptType: dbGPT.gpt_type || 'general', // Default to 'general' if not set
  createdAt: dbGPT.created_at,
  updatedAt: dbGPT.updated_at
});

// Helper function to convert frontend format to database fields
const mapGPTToDB = (gpt: GPT) => ({
  id: gpt.id,
  name: gpt.name,
  description: gpt.description,
  useCases: gpt.useCases || [],
  tags: gpt.tags || [],
  category: gpt.category,
  chatGptUrl: gpt.chatGptUrl,
  isPinned: gpt.isPinned || false,
  isActive: gpt.isActive !== false,
  gptType: gpt.gptType || 'general'
});

export const getGPTs = async (): Promise<GPT[]> => {
  try {
    const response = await fetch(`${API_BASE_URL}/gpts`);
    if (!response.ok) {
      throw new Error('Failed to fetch GPTs');
    }
    const data = await response.json();
    return data.map(mapGPTFromDB);
  } catch (error) {
    console.error('Error fetching GPTs:', error);
    return [];
  }
};

export const createGPT = async (gpt: GPT): Promise<GPT | null> => {
  try {
    const dbGPT = mapGPTToDB(gpt);
    const response = await fetch(`${API_BASE_URL}/gpts`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(dbGPT),
    });
    if (!response.ok) {
      throw new Error('Failed to create GPT');
    }
    const data = await response.json();
    return mapGPTFromDB(data);
  } catch (error) {
    console.error('Error creating GPT:', error);
    return null;
  }
};

export const updateGPT = async (id: string, gpt: GPT): Promise<GPT | null> => {
  try {
    const dbGPT = mapGPTToDB(gpt);
    const response = await fetch(`${API_BASE_URL}/gpts/${id}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(dbGPT),
    });
    if (!response.ok) {
      throw new Error('Failed to update GPT');
    }
    const data = await response.json();
    return mapGPTFromDB(data);
  } catch (error) {
    console.error('Error updating GPT:', error);
    return null;
  }
};

export const deleteGPT = async (id: string): Promise<boolean> => {
  try {
    const response = await fetch(`${API_BASE_URL}/gpts/${id}`, {
      method: 'DELETE',
    });
    return response.ok;
  } catch (error) {
    console.error('Error deleting GPT:', error);
    return false;
  }
};

export const updateGPTOrder = async (id: string, displayOrder: number): Promise<GPT | null> => {
  try {
    const response = await fetch(`${API_BASE_URL}/gpts/${id}/order`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ displayOrder }),
    });
    if (!response.ok) {
      throw new Error('Failed to update GPT order');
    }
    const data = await response.json();
    return mapGPTFromDB(data);
  } catch (error) {
    console.error('Error updating GPT order:', error);
    return null;
  }
};

export const getPrompts = async (): Promise<Prompt[]> => {
  try {
    const response = await fetch(`${API_BASE_URL}/prompts`);
    if (!response.ok) {
      throw new Error('Failed to fetch prompts');
    }
    const data = await response.json();
    return data.map((row: any) => ({
      id: row.id,
      title: row.title,
      description: row.description,
      prompt: row.prompt_text,
      category: row.category,
      tags: row.tags || [],
      createdAt: row.created_at,
      updatedAt: row.updated_at
    }));
  } catch (error) {
    console.error('Error fetching prompts:', error);
    return [];
  }
};

export const createPrompt = async (prompt: Prompt): Promise<Prompt | null> => {
  try {
    const response = await fetch(`${API_BASE_URL}/prompts`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(prompt),
    });
    if (!response.ok) {
      throw new Error('Failed to create prompt');
    }
    const data = await response.json();
    return {
      id: data.id,
      title: data.title,
      description: data.description,
      prompt: data.prompt_text,
      category: data.category,
      tags: data.tags || [],
      createdAt: data.created_at,
      updatedAt: data.updated_at
    };
  } catch (error) {
    console.error('Error creating prompt:', error);
    return null;
  }
};

export const updatePrompt = async (id: string, prompt: Prompt): Promise<Prompt | null> => {
  try {
    const response = await fetch(`${API_BASE_URL}/prompts/${id}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(prompt),
    });
    if (!response.ok) {
      throw new Error('Failed to update prompt');
    }
    const data = await response.json();
    return {
      id: data.id,
      title: data.title,
      description: data.description,
      prompt: data.prompt_text,
      category: data.category,
      tags: data.tags || [],
      createdAt: data.created_at,
      updatedAt: data.updated_at
    };
  } catch (error) {
    console.error('Error updating prompt:', error);
    return null;
  }
};

export const deletePrompt = async (id: string): Promise<boolean> => {
  try {
    const response = await fetch(`${API_BASE_URL}/prompts/${id}`, {
      method: 'DELETE',
    });
    return response.ok;
  } catch (error) {
    console.error('Error deleting prompt:', error);
    return false;
  }
};

export const getUsers = async (): Promise<User[]> => {
  try {
    const response = await fetch(`${API_BASE_URL}/users`);
    if (!response.ok) {
      throw new Error('Failed to fetch users');
    }
    const data = await response.json();
    return data.map((row: any) => ({
      id: row.id,
      email: row.email,
      name: row.name,
      role: row.role,
      password: '', // Password not returned from API for security
      createdAt: row.created_at,
      first_login: row.first_login,
      lastLogin: row.last_login || row.lastLogin
    }));
  } catch (error) {
    console.error('Error fetching users:', error);
    return [];
  }
};

export const createUser = async (user: Omit<User, 'id'> & { id?: string }): Promise<User | null> => {
  try {
    const userId = user.id || Date.now().toString();
    const response = await fetch(`${API_BASE_URL}/users`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        id: userId,
        email: user.email,
        name: user.name,
        password: user.password,
        role: user.role
      }),
    });
    if (!response.ok) {
      throw new Error('Failed to create user');
    }
    const data = await response.json();
    return {
      id: data.id,
      email: data.email,
      name: data.name,
      role: data.role,
      password: '', // Password not returned from API
      createdAt: data.created_at
    };
  } catch (error) {
    console.error('Error creating user:', error);
    return null;
  }
};

export const updateUser = async (id: string, user: User): Promise<User | null> => {
  try {
    const response = await fetch(`${API_BASE_URL}/users/${id}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        email: user.email,
        name: user.name,
        password: user.password,
        role: user.role
      }),
    });
    if (!response.ok) {
      throw new Error('Failed to update user');
    }
    const data = await response.json();
    return {
      id: data.id,
      email: data.email,
      name: data.name,
      role: data.role,
      password: '', // Password not returned from API
      createdAt: data.created_at
    };
  } catch (error) {
    console.error('Error updating user:', error);
    return null;
  }
};

export const deleteUser = async (id: string): Promise<boolean> => {
  try {
    const response = await fetch(`${API_BASE_URL}/users/${id}`, {
      method: 'DELETE',
    });
    return response.ok;
  } catch (error) {
    console.error('Error deleting user:', error);
    return false;
  }
};

export const authenticateUser = async (email: string, password: string): Promise<{ user: User | null; error?: string }> => {
  try {
    const response = await fetch(`${API_BASE_URL}/auth/login`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include', // Include cookies in request
      body: JSON.stringify({ email, password }),
    });
    
    if (!response.ok) {
      const errorData = await response.json();
      return { user: null, error: errorData.error };
    }
    
    const data = await response.json();
    return {
      user: {
        id: data.id,
        email: data.email,
        name: data.name,
        role: data.role,
        password: '', // Password not returned from API for security
        createdAt: data.created_at,
        first_login: data.first_login,
        lastLogin: data.last_login
      }
    };
  } catch (error) {
    console.error('Error authenticating user:', error);
    return { user: null, error: 'NETWORK_ERROR' };
  }
};

export const changePassword = async (userId: string, newPassword: string, currentPassword?: string): Promise<{ success: boolean; error?: string }> => {
  try {
    const response = await fetch(`${API_BASE_URL}/users/${userId}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        password: newPassword,
        currentPassword: currentPassword
      }),
    });
    
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      return { success: false, error: errorData.error || 'Failed to change password' };
    }
    
    return { success: true };
  } catch (error) {
    console.error('Error changing password:', error);
    return { success: false, error: 'Network error' };
  }
};

// GPT Registration Functions
export const getRegistrations = async (): Promise<GPTRegistration[]> => {
  try {
    const response = await fetch(`${API_BASE_URL}/registrations`);
    if (!response.ok) {
      throw new Error('Failed to fetch registrations');
    }
    const data = await response.json();
    return data.map((reg: any) => ({
      id: reg.id,
      firstName: reg.first_name,
      lastName: reg.last_name,
      email: reg.email, // ChatGPT email
      loginEmail: reg.login_email, // Login email
      status: reg.status,
      inviteSent: reg.invite_sent,
      selectedForExport: reg.selected_for_export,
      createdAt: reg.created_at,
      updatedAt: reg.updated_at
    }));
  } catch (error) {
    console.error('Error fetching registrations:', error);
    return [];
  }
};

export const createRegistration = async (registration: Omit<GPTRegistration, 'id' | 'status' | 'inviteSent' | 'selectedForExport' | 'createdAt' | 'updatedAt'>): Promise<GPTRegistration | null> => {
  try {
    const response = await fetch(`${API_BASE_URL}/registrations`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        firstName: registration.firstName,
        lastName: registration.lastName,
        email: registration.email, // ChatGPT email
        loginEmail: registration.loginEmail // Login email
      }),
    });
    if (!response.ok) {
      throw new Error('Failed to create registration');
    }
    const data = await response.json();
    return {
      id: data.id,
      firstName: data.first_name,
      lastName: data.last_name,
      email: data.email, // ChatGPT email
      loginEmail: data.login_email, // Login email
      status: data.status,
      inviteSent: data.invite_sent,
      selectedForExport: data.selected_for_export,
      createdAt: data.created_at,
      updatedAt: data.updated_at
    };
  } catch (error) {
    console.error('Error creating registration:', error);
    return null;
  }
};

export const updateRegistration = async (id: string, updates: {
  status?: 'active' | 'inactive';
  inviteSent?: boolean;
  selectedForExport?: boolean;
}): Promise<GPTRegistration | null> => {
  try {
    const response = await fetch(`${API_BASE_URL}/registrations/${id}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(updates),
    });
    if (!response.ok) {
      throw new Error('Failed to update registration');
    }
    const data = await response.json();
    return {
      id: data.id,
      firstName: data.first_name,
      lastName: data.last_name,
      email: data.email,
      status: data.status,
      inviteSent: data.invite_sent,
      selectedForExport: data.selected_for_export,
      createdAt: data.created_at,
      updatedAt: data.updated_at
    };
  } catch (error) {
    console.error('Error updating registration:', error);
    return null;
  }
};

export const deleteRegistration = async (id: string): Promise<boolean> => {
  try {
    const response = await fetch(`${API_BASE_URL}/registrations/${id}`, {
      method: 'DELETE',
    });
    return response.ok;
  } catch (error) {
    console.error('Error deleting registration:', error);
    return false;
  }
};

export const exportRegistrations = async (selectedOnly: boolean = false): Promise<string | null> => {
  try {
    const url = selectedOnly 
      ? `${API_BASE_URL}/registrations/export?selectedOnly=true`
      : `${API_BASE_URL}/registrations/export`;
    
    const response = await fetch(url);
    if (!response.ok) {
      throw new Error('Failed to export registrations');
    }
    return await response.text();
  } catch (error) {
    console.error('Error exporting registrations:', error);
    return null;
  }
};

// Suggested Prompts API functions
export const getSuggestedPrompts = async (): Promise<SuggestedPrompt[]> => {
  try {
    const response = await fetch(`${API_BASE_URL}/suggested-prompts`);
    if (!response.ok) {
      throw new Error('Failed to fetch suggested prompts');
    }
    const data = await response.json();
    return data.map((row: any) => ({
      id: row.id,
      title: row.title,
      description: row.description,
      prompt: row.prompt_text,
      category: row.category,
      tags: row.tags || [],
      submittedBy: row.submitted_by,
      submittedAt: row.submitted_at,
      status: row.status
    }));
  } catch (error) {
    console.error('Error fetching suggested prompts:', error);
    return [];
  }
};

export const createSuggestedPrompt = async (prompt: Omit<SuggestedPrompt, 'id' | 'submittedAt' | 'status'>): Promise<SuggestedPrompt | null> => {
  try {
    const response = await fetch(`${API_BASE_URL}/suggested-prompts`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        title: prompt.title,
        description: prompt.description,
        prompt_text: prompt.prompt,
        category: prompt.category,
        tags: prompt.tags,
        submitted_by: prompt.submittedBy
      }),
    });
    if (!response.ok) {
      throw new Error('Failed to create suggested prompt');
    }
    const data = await response.json();
    return {
      id: data.id,
      title: data.title,
      description: data.description,
      prompt: data.prompt_text,
      category: data.category,
      tags: data.tags || [],
      submittedBy: data.submitted_by,
      submittedAt: data.submitted_at,
      status: data.status
    };
  } catch (error) {
    console.error('Error creating suggested prompt:', error);
    return null;
  }
};

export const updateSuggestedPrompt = async (id: string, prompt: Partial<SuggestedPrompt>): Promise<SuggestedPrompt | null> => {
  try {
    const response = await fetch(`${API_BASE_URL}/suggested-prompts/${id}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        title: prompt.title,
        description: prompt.description,
        prompt_text: prompt.prompt,
        category: prompt.category,
        tags: prompt.tags,
        status: prompt.status
      }),
    });
    if (!response.ok) {
      throw new Error('Failed to update suggested prompt');
    }
    const data = await response.json();
    return {
      id: data.id,
      title: data.title,
      description: data.description,
      prompt: data.prompt_text,
      category: data.category,
      tags: data.tags || [],
      submittedBy: data.submitted_by,
      submittedAt: data.submitted_at,
      status: data.status
    };
  } catch (error) {
    console.error('Error updating suggested prompt:', error);
    return null;
  }
};

export const deleteSuggestedPrompt = async (id: string): Promise<boolean> => {
  try {
    const response = await fetch(`${API_BASE_URL}/suggested-prompts/${id}`, {
      method: 'DELETE',
    });
    return response.ok;
  } catch (error) {
    console.error('Error deleting suggested prompt:', error);
    return false;
  }
};

export const approveSuggestedPrompt = async (id: string): Promise<{ message: string; prompt: any } | null> => {
  try {
    const response = await fetch(`${API_BASE_URL}/suggested-prompts/${id}/approve`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
    });
    if (!response.ok) {
      throw new Error('Failed to approve suggested prompt');
    }
    return await response.json();
  } catch (error) {
    console.error('Error approving suggested prompt:', error);
    return null;
  }
};

// Category Management Functions
export const getCategories = async (): Promise<Category[]> => {
  try {
    const response = await fetch(`${API_BASE_URL}/categories`);
    if (!response.ok) {
      throw new Error('Failed to fetch categories');
    }
    const data = await response.json();
    return data.map((cat: any) => ({
      id: cat.id,
      name: cat.name,
      displayName: cat.display_name,
      description: cat.description,
      displayOrder: cat.display_order,
      isActive: cat.is_active,
      createdAt: cat.created_at,
      updatedAt: cat.updated_at
    }));
  } catch (error) {
    console.error('Error fetching categories:', error);
    return [];
  }
};

export const createCategory = async (category: Omit<Category, 'id' | 'createdAt' | 'updatedAt'>): Promise<Category | null> => {
  try {
    const response = await fetch(`${API_BASE_URL}/categories`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        name: category.name,
        displayName: category.displayName,
        description: category.description,
        displayOrder: category.displayOrder,
        isActive: category.isActive
      }),
    });
    if (!response.ok) {
      throw new Error('Failed to create category');
    }
    const data = await response.json();
    return {
      id: data.id,
      name: data.name,
      displayName: data.display_name,
      description: data.description,
      displayOrder: data.display_order,
      isActive: data.is_active,
      createdAt: data.created_at,
      updatedAt: data.updated_at
    };
  } catch (error) {
    console.error('Error creating category:', error);
    return null;
  }
};

export const updateCategory = async (id: string, category: Partial<Category>): Promise<Category | null> => {
  try {
    const response = await fetch(`${API_BASE_URL}/categories/${id}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        name: category.name,
        displayName: category.displayName,
        description: category.description,
        displayOrder: category.displayOrder,
        isActive: category.isActive
      }),
    });
    if (!response.ok) {
      throw new Error('Failed to update category');
    }
    const data = await response.json();
    return {
      id: data.id,
      name: data.name,
      displayName: data.display_name,
      description: data.description,
      displayOrder: data.display_order,
      isActive: data.is_active,
      createdAt: data.created_at,
      updatedAt: data.updated_at
    };
  } catch (error) {
    console.error('Error updating category:', error);
    return null;
  }
};

export const deleteCategory = async (id: string): Promise<boolean> => {
  try {
    const response = await fetch(`${API_BASE_URL}/categories/${id}`, {
      method: 'DELETE',
    });
    return response.ok;
  } catch (error) {
    console.error('Error deleting category:', error);
    return false;
  }
};

export const updateCategoryOrder = async (id: string, displayOrder: number): Promise<Category | null> => {
  try {
    const response = await fetch(`${API_BASE_URL}/categories/${id}/order`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ displayOrder }),
    });
    if (!response.ok) {
      throw new Error('Failed to update category order');
    }
    const data = await response.json();
    return {
      id: data.id,
      name: data.name,
      displayName: data.display_name,
      description: data.description,
      displayOrder: data.display_order,
      isActive: data.is_active,
      createdAt: data.created_at,
      updatedAt: data.updated_at
    };
  } catch (error) {
    console.error('Error updating category order:', error);
    return null;
  }
}; 